import { ArrowButtonDownBold, ArrowButtonUpBold } from '@ha/icons';
import React from 'react';
import { makeStyles } from '@material-ui/core';

import { useIntl } from 'ha/i18n';
import { Button, Chip, Divider, Popover, Typography, units } from '@ha/dsl';

import DropdownMenuItem from './DropdownMenuItem';
import { Menu } from '../utils';

const useStyles = makeStyles(theme => ({
  menu: {
    padding: units.yx(1, 1.5),
  },
  divider: {
    padding: units.yx(1, 0),
  },
  link: {
    paddingTop: units.rem(0.5),
  },
  chip: {
    marginLeft: units.rem(0.625),
  },
  chipRoot: {
    background: theme.palette.orange.main,
    color: 'white',
  },
}));

interface Props {
  id: string;
  title: string;
  data: Menu[];
  testId: string;
}

const DropdownMenu: React.FC<Props> = ({ id, title, data, testId }) => {
  const intl = useIntl();
  const { T } = intl;
  const classnames = useStyles();

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const setAnchorElement = React.useCallback<
    React.MouseEventHandler<HTMLButtonElement>
  >(event => setAnchorEl(event.currentTarget), []);

  const removeAnchorElement = React.useCallback(() => setAnchorEl(null), []);

  const isOpen = Boolean(anchorEl);

  return (
    <React.Fragment>
      <Button
        color="inherit"
        aria-owns={isOpen ? id : undefined}
        aria-haspopup="true"
        data-test-locator={`${testId} Btn`}
        onClick={setAnchorElement}
        iconSize="small"
        endIcon={isOpen ? <ArrowButtonUpBold /> : <ArrowButtonDownBold />}
      >
        {title}
      </Button>
      <Popover
        id={id}
        open={isOpen}
        keepMounted
        disablePortal
        onClose={removeAnchorElement}
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        PaperProps={{ elevation: 10 }}
      >
        <div className={classnames.menu} data-test-locator={`${testId} Menu`}>
          {data.map((section, index) => (
            <React.Fragment key={section.title}>
              {!!index && (
                <div className={classnames.divider}>
                  <Divider />
                </div>
              )}

              <Typography.H6>
                {section.title}
                {section.isNew && (
                  <span className={classnames.chip}>
                    <Chip
                      classes={{ root: classnames.chipRoot }}
                      label={T('New')}
                      size="small"
                    />
                  </span>
                )}
              </Typography.H6>

              {section.links.map(link => (
                <div className={classnames.link} key={link.title}>
                  <DropdownMenuItem {...link} />
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
      </Popover>
    </React.Fragment>
  );
};

export default DropdownMenu;
